import axios from 'axios';

/**
 * Custom error class for internal/expected errors that should not trigger console.error
 */
class SilentError extends Error {
  constructor(message, statusCode, isInternal = true) {
    super(message);
    this.name = 'SilentError';
    this.statusCode = statusCode;
    this.isInternal = isInternal;
    // Prevent stack trace from being logged
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SilentError);
    }
  }
}

/**
 * Maps Anthropic API error status codes to user-friendly error messages.
 * @param {number} statusCode - The HTTP status code from Anthropic API.
 * @param {Object} errorData - The error data from API response.
 * @returns {Object} Object with isInternal flag and error message.
 */
function getErrorMessage(statusCode, errorData) {
  if (statusCode === 401) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your Anthropic API key.' };
  } else if (statusCode === 402) {
    return { isInternal: true, message: 'Insufficient credits. Your Anthropic API credit balance is too low. Please add credits to your account.' };
  } else if (statusCode === 403) {
    return { isInternal: true, message: 'Permission denied. Your API key does not have access to the specified resource.' };
  } else if (statusCode === 404) {
    return { isInternal: true, message: 'Resource not found. The requested endpoint or model may not exist.' };
  } else if (statusCode === 429) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (statusCode === 500) {
    return { isInternal: true, message: 'Anthropic service error. An unexpected error occurred on their servers. Please try again later.' };
  } else if (statusCode === 529) {
    return { isInternal: true, message: 'Anthropic service is temporarily overloaded. Please try again in a few moments.' };
  } else {
    return { isInternal: false, message: errorData?.error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

const API_URL = 'https://api.anthropic.com/v1/messages';
const API_VERSION = '2023-06-01';
const MODEL = 'claude-sonnet-4-5-20250929';

/**
 * Translates intimate desires/preferences into elegant, partner-appropriate language
 * @param {string} rawText - The original intimate text from user
 * @param {string} category - Category of the desire (desire, concern, boundary, preference)
 * @returns {Promise<Object>} Object containing translated text and tone
 */
export const translateDesire = async (rawText, category) => {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;

  if (!apiKey || apiKey === 'your-anthropic-api-key-here') {
    throw new Error('Anthropic API key is not configured. Please add your API key to the .env file.');
  }

  const systemPrompt = `You are an expert relationship communication assistant specializing in translating intimate desires and preferences into elegant, respectful, and partner-appropriate language. Your role is to:

1. Preserve the core meaning and intent of the original message
2. Transform casual or explicit language into thoughtful, sophisticated expressions
3. Maintain emotional authenticity while elevating the tone
4. Ensure the message feels warm, inviting, and non-threatening
5. Keep the translation concise (2-4 sentences maximum)

The category of this message is: ${category}

Respond ONLY with the translated text. Do not include explanations, introductions, or meta-commentary.`;

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  const data = {
    model: MODEL,
    max_tokens: 500,
    temperature: 0.7,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: `Translate this intimate message into elegant, partner-appropriate language:\n\n"${rawText}"`,
      },
    ],
  };

  try {
    const response = await axios?.post(API_URL, data, { headers });
    
    if (response?.data?.content?.[0]?.type === 'text') {
      return {
        translatedText: response?.data?.content?.[0]?.text?.trim(),
        originalText: rawText,
        category: category,
        timestamp: new Date()?.toISOString(),
      };
    }

    throw new Error('Unexpected response format from Anthropic API');
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      
      // Use SilentError for internal/expected errors to prevent console logging
      if (errorInfo?.isInternal) {
        throw new SilentError(errorInfo.message, error?.response?.status, true);
      }
      
      // For unexpected errors, log and throw regular error
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      console.error('Anthropic API error:', apiError);
      throw apiError;
    }
    throw error;
  }
};

/**
 * Generates AI insights for compatibility based on conversation history
 * @param {Array} conversationHistory - Array of conversation messages
 * @returns {Promise<string>} AI-generated insight
 */
export const generateCompatibilityInsight = async (conversationHistory) => {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;

  if (!apiKey || apiKey === 'your-anthropic-api-key-here') {
    throw new Error('Anthropic API key is not configured. Please add your API key to the .env file.');
  }

  const systemPrompt = `You are a relationship compatibility analyst. Analyze the conversation patterns and provide a brief, actionable insight about the couple's communication compatibility. Focus on strengths and gentle suggestions for improvement. Keep your response to 2-3 sentences.`;

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  const conversationSummary = conversationHistory?.map((msg, idx) => `Message ${idx + 1}: ${msg?.text}`)?.join('\n');

  const data = {
    model: MODEL,
    max_tokens: 300,
    temperature: 0.6,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: `Analyze this conversation and provide compatibility insights:\n\n${conversationSummary}`,
      },
    ],
  };

  try {
    const response = await axios?.post(API_URL, data, { headers });
    
    if (response?.data?.content?.[0]?.type === 'text') {
      return response?.data?.content?.[0]?.text?.trim();
    }

    throw new Error('Unexpected response format from Anthropic API');
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', apiError);
      }
      throw apiError;
    }
    throw error;
  }
};

/**
 * Generates comprehensive analytics insights for relationship patterns
 * @param {Object} analyticsData - Object containing compatibility scores, trends, and preferences
 * @returns {Promise<Array>} Array of AI-generated recommendations
 */
export const generateAnalyticsInsights = async (analyticsData) => {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;

  if (!apiKey || apiKey === 'your-anthropic-api-key-here') {
    throw new Error('Anthropic API key is not configured. Please add your API key to the .env file.');
  }

  const systemPrompt = `You are an expert relationship analytics AI specializing in intimate relationship patterns, compatibility analysis, and personalized recommendations. Your role is to:

1. Analyze compatibility scores, intimacy trends, and preference alignment data
2. Identify patterns, opportunities, and areas for growth
3. Generate 3-5 specific, actionable recommendations prioritized by impact
4. Provide timing suggestions based on mood and availability patterns
5. Maintain a supportive, non-judgmental, and encouraging tone

For each recommendation, include:
- Priority level (high/medium/low)
- Specific actionable advice
- Expected impact on relationship quality
- Relevant timing or implementation suggestions

Keep recommendations concise (2-3 sentences each) and focused on deepening connection.`;

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  const dataSummary = `
Compatibility Scores:
${analyticsData?.compatibilityScores?.map(s => `- ${s?.category}: ${s?.value}% (trend: ${s?.trend > 0 ? '+' : ''}${s?.trend}%)`)?.join('\n')}

Recent Intimacy Trends:
- Average frequency: ${(analyticsData?.intimacyTrends?.reduce((sum, item) => sum + item?.frequency, 0) / analyticsData?.intimacyTrends?.length)?.toFixed(1)} per week
- Average satisfaction: ${(analyticsData?.intimacyTrends?.reduce((sum, item) => sum + item?.satisfaction, 0) / analyticsData?.intimacyTrends?.length)?.toFixed(1)}/10
- Mood correlation: ${(analyticsData?.intimacyTrends?.reduce((sum, item) => sum + item?.mood, 0) / analyticsData?.intimacyTrends?.length)?.toFixed(1)}/10

Preference Alignment:
${analyticsData?.preferenceAlignment?.map(p => `- ${p?.preference}: Partner 1 (${p?.partner1}%) vs Partner 2 (${p?.partner2}%)`)?.join('\n')}

Time Range: ${analyticsData?.timeRange}`;

  const data = {
    model: MODEL,
    max_tokens: 1500,
    temperature: 0.7,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: `Analyze this relationship data and provide personalized recommendations for deepening connection:\n\n${dataSummary}`,
      },
    ],
  };

  try {
    const response = await axios?.post(API_URL, data, { headers });
    
    if (response?.data?.content?.[0]?.type === 'text') {
      // Return the raw text - the component will handle formatting
      return response?.data?.content?.[0]?.text?.trim();
    }

    throw new Error('Unexpected response format from Anthropic API');
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', apiError);
      }
      throw apiError;
    }
    throw error;
  }
};

/**
 * Generates intelligent notification message for partner mood changes
 * @param {Object} moodData - Current mood data including mood, energy, emotional state
 * @param {Object} partnerHistory - Partner's mood history and patterns
 * @param {Object} relationshipContext - Compatibility scores and preference patterns
 * @returns {Promise<Object>} Notification with personalized message and timing suggestion
 */
export const generateMoodChangeNotification = async (moodData, partnerHistory, relationshipContext) => {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;

  if (!apiKey || apiKey === 'your-anthropic-api-key-here') {
    throw new Error('Anthropic API key is not configured. Please add your API key to the .env file.');
  }

  const systemPrompt = `You are an intelligent relationship notification assistant. Generate a brief, personalized notification message (1-2 sentences) about a partner's mood change that:

1. Highlights the significance of the mood shift
2. Suggests optimal timing for connection based on patterns
3. Maintains a warm, encouraging tone
4. Includes actionable next steps

Respond with a JSON object containing:
- "message": The notification text
- "timingSuggestion": Specific timing recommendation (e.g., "within the next 2 hours", "this evening around 7-9pm")
- "priority": "high", "medium", or "low" -"suggestedAction": Brief action suggestion`;

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  const contextSummary = `
Current Mood: ${moodData?.mood} (Energy: ${moodData?.energy}/10, Emotional State: ${moodData?.emotionalState})
Partner's Recent Pattern: ${partnerHistory?.recentMoods?.join(', ') || 'adventurous, playful, intimate'}
Compatibility Score: ${relationshipContext?.compatibilityScore || 85}%
Preferred Connection Times: ${relationshipContext?.preferredTimes?.join(', ') || 'evenings, weekends'}
Last Intimate Encounter: ${relationshipContext?.lastEncounter || '3 days ago'}`;

  const data = {
    model: MODEL,
    max_tokens: 300,
    temperature: 0.7,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: `Generate an intelligent notification for this mood change:${contextSummary}`,
      },
    ],
  };

  try {
    const response = await axios?.post(API_URL, data, { headers });
    
    if (response?.data?.content?.[0]?.type === 'text') {
      const responseText = response?.data?.content?.[0]?.text?.trim();
      
      // Try to parse JSON response
      try {
        const parsed = JSON.parse(responseText);
        return {
          message: parsed?.message,
          timingSuggestion: parsed?.timingSuggestion,
          priority: parsed?.priority || 'medium',
          suggestedAction: parsed?.suggestedAction,
          timestamp: new Date()?.toISOString(),
        };
      } catch {
        // Fallback if not JSON
        return {
          message: responseText,
          timingSuggestion: 'soon',
          priority: 'medium',
          suggestedAction: 'Check mood tracker',
          timestamp: new Date()?.toISOString(),
        };
      }
    }

    throw new Error('Unexpected response format from Anthropic API');
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', apiError);
      }
      throw apiError;
    }
    throw error;
  }
};

/**
 * Generates intelligent notification message for partner availability changes
 * @param {Object} availabilityData - New availability windows and preferences
 * @param {Object} scheduleHistory - Historical scheduling patterns
 * @param {Object} relationshipContext - Compatibility and preference data
 * @returns {Promise<Object>} Notification with personalized message and optimal timing
 */
export const generateAvailabilityNotification = async (availabilityData, scheduleHistory, relationshipContext) => {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;

  if (!apiKey || apiKey === 'your-anthropic-api-key-here') {
    throw new Error('Anthropic API key is not configured. Please add your API key to the .env file.');
  }

  const systemPrompt = `You are an intelligent relationship scheduling assistant. Generate a brief, personalized notification message (1-2 sentences) about a partner's availability change that:

1. Highlights new opportunities for connection
2. Suggests optimal time slots based on both partners' patterns
3. Considers compatibility scores and past successful encounters
4. Maintains an inviting, non-pressuring tone

Respond with a JSON object containing:
- "message": The notification text
- "optimalTimeSlots": Array of 2-3 specific time suggestions (e.g., ["Tonight 8-10pm", "Tomorrow morning 10am-12pm"])
- "priority": "high", "medium", or "low" -"compatibilityReason": Brief explanation of why these times are optimal`;

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  const contextSummary = `
New Availability: ${availabilityData?.newSlots?.join(', ') || 'Evening slots opened'}
Partner's Typical Schedule: ${scheduleHistory?.typicalAvailability?.join(', ') || 'evenings, weekends'}
Your Availability: ${availabilityData?.userAvailability?.join(', ') || 'flexible evenings'}
Compatibility Score: ${relationshipContext?.compatibilityScore || 85}%
Most Successful Past Times: ${scheduleHistory?.successfulTimes?.join(', ') || 'Friday evenings, Sunday mornings'}
Current Mood Alignment: ${relationshipContext?.moodAlignment || 'high'}`;

  const data = {
    model: MODEL,
    max_tokens: 400,
    temperature: 0.7,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: `Generate an intelligent notification for this availability change:${contextSummary}`,
      },
    ],
  };

  try {
    const response = await axios?.post(API_URL, data, { headers });
    
    if (response?.data?.content?.[0]?.type === 'text') {
      const responseText = response?.data?.content?.[0]?.text?.trim();
      
      // Try to parse JSON response
      try {
        const parsed = JSON.parse(responseText);
        return {
          message: parsed?.message,
          optimalTimeSlots: parsed?.optimalTimeSlots || [],
          priority: parsed?.priority || 'medium',
          compatibilityReason: parsed?.compatibilityReason,
          timestamp: new Date()?.toISOString(),
        };
      } catch {
        // Fallback if not JSON
        return {
          message: responseText,
          optimalTimeSlots: ['This evening', 'Tomorrow'],
          priority: 'medium',
          compatibilityReason: 'Based on your patterns',
          timestamp: new Date()?.toISOString(),
        };
      }
    }

    throw new Error('Unexpected response format from Anthropic API');
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      
      // Use SilentError for internal/expected errors to prevent console logging
      if (errorInfo?.isInternal) {
        throw new SilentError(errorInfo.message, error?.response?.status, true);
      }
      
      // For unexpected errors, log and throw regular error
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      console.error('Anthropic API error:', apiError);
      throw apiError;
    }
    throw error;
  }
};

/**
 * Generates proactive encounter suggestions based on mood patterns, availability history, and preferences
 * @param {Object} moodPatterns - Historical mood data and patterns
 * @param {Object} availabilityHistory - Past scheduling patterns and success rates
 * @param {Object} preferences - User preferences for encounters
 * @param {number} lookAheadDays - Number of days to look ahead for suggestions
 * @returns {Promise<Array>} Array of proactive encounter suggestions with compatibility scores
 */
export const generateProactiveEncounterSuggestions = async (moodPatterns, availabilityHistory, preferences, lookAheadDays = 7) => {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;

  if (!apiKey || apiKey === 'your-anthropic-api-key-here') {
    throw new Error('Anthropic API key is not configured. Please add your API key to the .env file.');
  }

  const systemPrompt = `You are an expert relationship AI specializing in proactive encounter planning. Analyze mood patterns, availability history, and preferences to suggest optimal encounter windows BEFORE users even open the app.

Your role is to:
1. Identify patterns in mood cycles, energy levels, and emotional states
2. Recognize successful scheduling patterns from history
3. Predict optimal windows based on historical data
4. Generate 3-5 specific, actionable suggestions with high compatibility scores
5. Provide clear reasoning for each suggestion

For each suggestion, provide:
- day: Day of week or specific date
- time: Specific time (e.g., "19:30", "20:00")
- duration: Suggested duration in minutes
- compatibilityScore: 0-100 score based on pattern analysis
- reasoning: Brief explanation of why this time is optimal
- moodPrediction: Expected mood alignment
- energyPrediction: Expected energy levels

Respond with a JSON array of suggestions, ordered by compatibility score (highest first).`;

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  const contextSummary = `
Mood Patterns (Last 30 Days):
- Average Mood: ${moodPatterns?.averageMood}
- Average Energy: ${moodPatterns?.averageEnergy}/5
- Preferred Encounter Types: ${moodPatterns?.preferredEncounterTypes?.join(', ')}
- Peak Days: ${moodPatterns?.peakDays?.join(', ')}
- Peak Times: ${moodPatterns?.peakTimes?.join(', ')}
- Total Mood Entries: ${moodPatterns?.totalEntries}

Availability History:
- Preferred Days: ${availabilityHistory?.preferredDays?.join(', ')}
- Preferred Times: ${availabilityHistory?.preferredTimes?.join(', ')}
- Average Duration: ${availabilityHistory?.averageDuration} minutes
- Success Rate: ${availabilityHistory?.successRate?.toFixed(1)}%
- Total Scheduled: ${availabilityHistory?.totalScheduled}

User Preferences:
- Preferred Locations: ${preferences?.preferredLocations?.join(', ')}
- Timing Flexibility: ${preferences?.timingFlexibility}
- Notification Preference: ${preferences?.notificationPreference}

Look Ahead: Next ${lookAheadDays} days
Current Date: ${new Date()?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`;

  const data = {
    model: MODEL,
    max_tokens: 2000,
    temperature: 0.7,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: `Analyze these patterns and generate proactive encounter suggestions for the next ${lookAheadDays} days:\n\n${contextSummary}`,
      },
    ],
  };

  try {
    const response = await axios?.post(API_URL, data, { headers });
    
    if (response?.data?.content?.[0]?.type === 'text') {
      const responseText = response?.data?.content?.[0]?.text?.trim();
      
      // Try to parse JSON response
      try {
        // Extract JSON from response (might be wrapped in markdown code blocks)
        const jsonMatch = responseText?.match(/\[\s*\{[\s\S]*\}\s*\]/);
        const jsonText = jsonMatch ? jsonMatch?.[0] : responseText;
        const parsed = JSON.parse(jsonText);
        
        // Ensure each suggestion has required fields
        return parsed?.map((suggestion, index) => ({
          id: `proactive-${Date.now()}-${index}`,
          day: suggestion?.day || 'TBD',
          date: suggestion?.date || calculateFutureDate(suggestion?.day),
          time: suggestion?.time || '20:00',
          duration: suggestion?.duration || availabilityHistory?.averageDuration || 90,
          compatibilityScore: suggestion?.compatibilityScore || 75,
          reasoning: suggestion?.reasoning || 'Based on your patterns',
          moodPrediction: suggestion?.moodPrediction || 'Positive alignment expected',
          energyPrediction: suggestion?.energyPrediction || 'Good energy levels',
          source: 'proactive-ai',
          timestamp: new Date()?.toISOString()
        }));
      } catch (parseError) {
        console.error('Error parsing proactive suggestions:', parseError);
        // Return fallback suggestions
        return generateFallbackSuggestions(moodPatterns, availabilityHistory, lookAheadDays);
      }
    }

    throw new Error('Unexpected response format from Anthropic API');
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      
      // Use SilentError for internal/expected errors to prevent console logging
      if (errorInfo?.isInternal) {
        throw new SilentError(errorInfo.message, error?.response?.status, true);
      }
      
      // For unexpected errors, log and throw regular error
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      console.error('Anthropic API error:', apiError);
      throw apiError;
    }
    throw error;
  }
};

/**
 * Calculate future date from day name
 */
function calculateFutureDate(dayName) {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const today = new Date();
  const todayDay = today?.getDay();
  
  // Find target day index
  let targetDay = daysOfWeek?.findIndex(d => d?.toLowerCase() === dayName?.toLowerCase());
  if (targetDay === -1) targetDay = todayDay; // Default to today if not found
  
  // Calculate days until target
  let daysUntil = targetDay - todayDay;
  if (daysUntil <= 0) daysUntil += 7; // Next week if day has passed
  
  const futureDate = new Date(today);
  futureDate?.setDate(today?.getDate() + daysUntil);
  
  return futureDate?.toISOString();
}

/**
 * Generate fallback suggestions when AI fails
 */
function generateFallbackSuggestions(moodPatterns, availabilityHistory, lookAheadDays) {
  const suggestions = [];
  const peakDays = availabilityHistory?.preferredDays || ['Friday', 'Saturday'];
  const peakTimes = availabilityHistory?.preferredTimes || ['19:00', '20:00'];
  
  for (let i = 0; i < Math.min(3, peakDays?.length); i++) {
    suggestions?.push({
      id: `fallback-${Date.now()}-${i}`,
      day: peakDays?.[i],
      date: calculateFutureDate(peakDays?.[i]),
      time: peakTimes?.[i % peakTimes?.length],
      duration: availabilityHistory?.averageDuration || 90,
      compatibilityScore: 80 - (i * 5),
      reasoning: `Based on your successful ${peakDays?.[i]} patterns`,
      moodPrediction: 'Positive alignment expected',
      energyPrediction: 'Good energy levels',
      source: 'fallback',
      timestamp: new Date()?.toISOString()
    });
  }
  
  return suggestions;
}

// Export all functions at the end after they are defined
export default {
  translateDesire,
  generateCompatibilityInsight,
  generateAnalyticsInsights,
  generateMoodChangeNotification,
  generateAvailabilityNotification,
  generateProactiveEncounterSuggestions,
};